import React, { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CardContent,
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	AccountBalanceWallet,
	ArrowUpward,
	ContentCopy,
	Person,
	Warning,
} from '@mui/icons-material';
import {
	LaunchButton,
	Main,
	RiskButton,
	StatsCardInnerReverse,
	StyledCardReverse,
} from '../styles/commonStyles';
import { Header } from './Header';
import { convertFromBaseUnits } from 'core/vaultTransactions';
import { copyAccountAddress } from 'utils/clipboard';
import { styled } from '@mui/system';
import { AgentDetail, AGENTS_LIST } from 'data/Agents';

const StyledBox = styled('div')`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	background-color: var(--atlas-bg-surface-alt);
	border-radius: 1;
`;

interface EmptyDashboardViewProps {
	aptosBalance: number;
	usdcBalance: number;
	fetchingBalance: boolean;
	depositAmount: string;
	setDepositAmount: (amount: string) => void;
	hasActivePosition: boolean;
	onDeposit: () => void;
	activeAccount: any;
	agentsList: typeof AGENTS_LIST;
	selectedAgentId: number | null;
	onSelectAgent: (id: number) => void;
	minAmountAllowed: number;
	maxAmountAllowed: number;
}

export const EmptyDashboardView: React.FC<EmptyDashboardViewProps> = ({
	usdcBalance,
	fetchingBalance,
	depositAmount,
	setDepositAmount,
	hasActivePosition,
	onDeposit,
	activeAccount,
	aptosBalance,
	agentsList,
	selectedAgentId,
	onSelectAgent,
	minAmountAllowed,
	maxAmountAllowed,
}) => {
	const [discountCode, setDiscountCode] = useState('');
	const isBalanceLow =
		!fetchingBalance && convertFromBaseUnits(usdcBalance) < 0.1;
	const isAptosBalanceLow = !fetchingBalance && aptosBalance < 0.4;
	const selectedAgent = useMemo(
		() =>
			agentsList.find((agent) => agent.id === selectedAgentId) ||
			agentsList[0],
		[selectedAgentId, agentsList],
	);

	return (
		<Main>
			<Header />

			<Box
				maxWidth="1200px"
				width="100%"
				p={{ xs: 1.5, sm: 2, md: 3 }}
				mt={{ xs: 1, sm: 2 }}
			>
				<Grid container spacing={{ xs: 1.5, sm: 2 }}>
					<Grid item xs={12} sm={6}>
						<StyledCardReverse sx={{ position: 'relative' }}>
							<StatsCardInnerReverse>
								<CardContent
									sx={{
										paddingInline: {
											xs: '20px',
											sm: '24px',
										},
										paddingBlock: {
											xs: '16px !important',
											sm: '20px !important',
										},
									}}
								>
									<Typography
										variant="h6"
										sx={{
											fontSize: {
												xs: '1.25rem',
												sm: '1.6rem',
												md: '1.8rem',
											},
											fontWeight: 500,
											color: 'var(--atlas-accent-primary-glow)',
											marginBottom: '0',
										}}
									>
										Your Yield,
										<br /> Sir.
									</Typography>
									<Box
										component="img"
										src="/images/atlas-logo.png"
										alt="Dog"
										sx={{
											height: {
												xs: '80px',
												sm: '100px',
												md: '120px',
											},
											position: 'absolute',
											bottom: {
												xs: '-1.5rem',
												sm: '-2rem',
											},
											right: '0',
										}}
									/>
								</CardContent>
							</StatsCardInnerReverse>
						</StyledCardReverse>
					</Grid>
					<Grid
						item
						xs={12}
						sm={6}
						sx={{
							display: 'grid',
							gridTemplateColumns: {
								xs: '1fr 1fr',
								sm: '1fr 1fr',
							},
							gap: { xs: '12px', sm: '20px' },
						}}
					>
						<StyledCardReverse sx={{ position: 'relative' }}>
							<StatsCardInnerReverse>
								<CardContent
									sx={{
										padding: { xs: '18px', sm: '20px' },
										paddingBottom: {
											xs: '18px !important',
											sm: '20px !important',
										},
										backgroundColor: 'transparent',
										display: 'flex',
										gap: { xs: '12px', sm: '16px' },
										flexDirection: 'column',
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
											gap: '12px',
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												borderRadius: '50%',
												backgroundColor: '#275ff0',
												width: '44px',
												height: '44px',
												flexShrink: 0,
											}}
										>
											<AccountBalanceWallet
												style={{
													color: 'white',
													width: '22px',
													height: '22px',
												}}
											/>
										</div>
										<div>
											<Typography
												color="textPrimary"
												style={{
													fontSize: '14px',
												}}
											>
												TVL
											</Typography>
											<Typography
												color="textPrimary"
												variant="h6"
												fontFamily="Oxanium"
												fontSize={22}
											>
												-
											</Typography>
										</div>
									</div>
								</CardContent>
							</StatsCardInnerReverse>
						</StyledCardReverse>

						<StyledCardReverse sx={{ position: 'relative' }}>
							<StatsCardInnerReverse>
								<CardContent
									sx={{
										backgroundColor: 'transparent',
										padding: { xs: '18px', sm: '20px' },
										paddingBottom: {
											xs: '18px !important',
											sm: '20px !important',
										},
										display: 'flex',
										gap: { xs: '12px', sm: '16px' },
										flexDirection: 'column',
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
											gap: '12px',
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor:
													'var(--atlas-accent-purple)',
												borderRadius: '50%',
												width: '44px',
												height: '44px',
												flexShrink: 0,
											}}
										>
											<Person
												style={{
													color: 'white',
													width: '24px',
													height: '24px',
												}}
											/>
										</div>
										<div>
											<Typography
												color="textPrimary"
												style={{
													fontSize: '14px',
												}}
											>
												Users
											</Typography>
											<Typography
												color="textPrimary"
												variant="h6"
												fontFamily="Oxanium"
												fontSize={22}
											>
												-
											</Typography>
										</div>
									</div>
								</CardContent>
							</StatsCardInnerReverse>
						</StyledCardReverse>
					</Grid>
					<Grid item xs={12} sm={6}>
						<StyledCardReverse>
							<StatsCardInnerReverse>
								<CardContent
									sx={{
										width: '100%',
										height: '100%',
										padding: {
											xs: '16px',
											sm: '20px',
											md: '24px',
										},
									}}
								>
									<Typography
										color="textPrimary"
										variant="h6"
										gutterBottom
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '16px',
												sm: '18px',
												md: '20px',
											},
										}}
									>
										Your Strategy
									</Typography>
									{/*<Typography
									color="textSecondary"
									gutterBottom
									fontFamily="Oxanium"
									sx={{
										fontSize: {
											xs: '14px',
											sm: '15px',
											md: '16px',
										},
									}}
									mb={2}
								>
									Risk-to-reward ratio.
								</Typography>*/}

									<Box display="flex" flexDirection="column">
										{agentsList.map((agent) => (
											<RiskButton
												disabled={agent?.disabled}
												key={agent.id}
												selected={
													agent.id === selectedAgentId
												}
												fullWidth
												onClick={() =>
													onSelectAgent(agent.id)
												}
												sx={{
													justifyContent:
														'space-between',
													alignItems: 'center',
													backgroundColor:
														agent.id ===
														selectedAgentId
															? 'var(--atlas-accent-primary-glow-alpha-10)'
															: 'background.paper',
													borderColor:
														agent.id ===
														selectedAgentId
															? 'var(--atlas-accent-primary-alt)'
															: 'var(--atlas-border-soft)',
												}}
											>
												<Box
													component="span"
													alignItems="center"
													gap={1}
													textAlign="left"
													sx={{
														display: 'inline-flex',
													}}
												>
													<Typography
														color="textPrimary"
														variant="h6"
														fontFamily={'Oxanium'}
														fontWeight={700}
														component="span"
													>
														{agent.name}
													</Typography>
													<Box
														component="span"
														alignItems="center"
														gap={1}
														sx={{
															display:
																'inline-flex',
														}}
													>
														{agent.details.map(
															(
																d: AgentDetail,
															) => (
																<Tooltip
																	key={`${agent.id}-${d.name}`}
																	title={
																		d.name
																	}
																	arrow
																>
																	<Box
																		component="img"
																		src={
																			d.src
																		}
																		alt={
																			d.name
																		}
																		onClick={(
																			event,
																		) => {
																			event.stopPropagation();
																			window.open(
																				d.link,
																				'_blank',
																				'noopener,noreferrer',
																			);
																		}}
																		sx={{
																			width: 24,
																			height: 24,
																			borderRadius:
																				'50%',
																			objectFit:
																				'cover',
																			border: '1px solid',
																			borderColor:
																				agent.id ===
																				selectedAgentId
																					? 'rgba(255,255,255,0.24)'
																					: 'var(--atlas-border-soft)',
																			cursor: 'pointer',
																		}}
																	/>
																</Tooltip>
															),
														)}
													</Box>
												</Box>
												<Box textAlign="right">
													<Typography
														color="textPrimary"
														variant="body2"
														fontWeight={600}
													>
														{agent.yield}
													</Typography>
													{agent.id ===
														selectedAgentId && (
														<Typography
															color="textPrimary"
															variant="caption"
															sx={{
																fontWeight: 700,
																letterSpacing:
																	'0.03em',
															}}
														>
															Estimated
														</Typography>
													)}
												</Box>
											</RiskButton>
										))}
									</Box>
								</CardContent>
							</StatsCardInnerReverse>
						</StyledCardReverse>
					</Grid>

					<Grid item xs={12} sm={6}>
						<StyledCardReverse>
							<StatsCardInnerReverse>
								<CardContent
									sx={{
										backgroundColor: 'transparent',
										padding: {
											xs: '16px',
											sm: '20px',
											md: '24px',
										},
									}}
								>
									<Typography
										color="textPrimary"
										variant="h6"
										gutterBottom
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '16px',
												sm: '18px',
												md: '20px',
											},
										}}
									>
										Make Your First Deposit
									</Typography>
									<Typography
										color="textPrimary"
										gutterBottom
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '14px',
												sm: '15px',
												md: '16px',
											},
										}}
										mb={2}
									>
										Start earning automated yield on your
										USDC
									</Typography>

									{isBalanceLow && (
										<Alert
											severity="warning"
											sx={{ mb: 2 }}
											icon={<Warning />}
											action={
												<Tooltip
													title="Copy account address"
													arrow
												>
													<IconButton
														size="small"
														onClick={() =>
															activeAccount?.accountAddress &&
															copyAccountAddress(
																activeAccount.accountAddress.toString(),
															)
														}
														sx={{
															color: 'warning.main',
														}}
													>
														<ContentCopy fontSize="small" />
													</IconButton>
												</Tooltip>
											}
										>
											<Typography
												variant="body2"
												fontWeight={600}
											>
												Low Account Balance
											</Typography>
											<Typography variant="caption">
												Fund your account to start
												depositing.
											</Typography>
										</Alert>
									)}
									{isAptosBalanceLow && !isBalanceLow && (
										<Alert
											severity="warning"
											sx={{ mb: 2 }}
											icon={<Warning />}
										>
											<Typography
												variant="body2"
												fontWeight={600}
											>
												Low Aptos Balance
											</Typography>
											<Typography variant="caption">
												Fund your account to pay the gas
												fee.
											</Typography>
										</Alert>
									)}

									<StyledBox
										sx={{
											p: { xs: 1.5, sm: 2 },
											mb: 2,
										}}
									>
										<div>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
												flexWrap="wrap"
												gap={1}
											>
												<Typography
													color="textSecondary"
													variant="body2"
												>
													Available Balance
												</Typography>
											</Box>
											<Typography
												variant="h6"
												fontFamily="Oxanium"
												color="textPrimary"
											>
												{fetchingBalance
													? 'Loading...'
													: `${convertFromBaseUnits(
															usdcBalance,
													  ).toLocaleString(
															undefined,
															{
																minimumFractionDigits: 2,
																maximumFractionDigits: 6,
															},
													  )} USDC`}
											</Typography>
										</div>
										<div>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
												flexWrap="wrap"
												gap={1}
											>
												<Typography
													color="textSecondary"
													variant="body2"
												>
													Aptos Balance
												</Typography>
											</Box>
											<Typography
												variant="h6"
												fontFamily="Oxanium"
												color={
													aptosBalance < 0.4
														? 'error'
														: 'var(--atlas-text-primary)'
												}
											>
												{aptosBalance}
											</Typography>
										</div>
									</StyledBox>

									<TextField
										fullWidth
										label="Amount (USDC)"
										type="number"
										value={depositAmount}
										onChange={(e) =>
											setDepositAmount(e.target.value)
										}
										margin="normal"
										placeholder="50"
										size="small"
										sx={{
											'& .MuiInputLabel-root': {
												color: 'var(--atlas-text-muted-strong)',
											},
											'& .MuiInputLabel-root.Mui-focused':
												{
													color: 'var(--atlas-text-highlight)',
												},
											'& .MuiFormHelperText-root': {
												color: 'var(--atlas-text-subtle)',
											},
											'& .MuiOutlinedInput-root': {
												color: 'var(--atlas-text-primary)',
												backgroundColor:
													'var(--atlas-bg-surface-strong)',
												'& input[type=number]': {
													colorScheme: 'dark',
												},
												'& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
													{
														filter: 'invert(1)',
														opacity: 0.8,
													},
												'& fieldset': {
													borderColor:
														'var(--atlas-border-accent)',
												},
												'&:hover fieldset': {
													borderColor:
														'var(--atlas-border-accent-alt)',
												},
												'&.Mui-focused fieldset': {
													borderColor:
														'var(--atlas-accent-primary-alt)',
												},
												'&.Mui-disabled': {
													color: 'var(--atlas-text-subtle)',
													'& fieldset': {
														borderColor:
															'var(--atlas-border-accent-strong)',
													},
												},
												'& input::placeholder': {
													color: 'var(--atlas-text-subtle)',
													opacity: 1,
												},
											},
										}}
										helperText={`Min: ${minAmountAllowed} USDC | Max: ${maxAmountAllowed} USDC | Balance: ${
											fetchingBalance
												? '...'
												: convertFromBaseUnits(
														usdcBalance,
												  ).toFixed(2)
										} USDC`}
										InputProps={{
											endAdornment: (
												<Button
													size="small"
													onClick={() => {
														const maxAmount =
															Math.min(
																convertFromBaseUnits(
																	usdcBalance,
																),
																maxAmountAllowed,
															);

														const truncated =
															Math.floor(
																maxAmount * 100,
															) / 100;
														setDepositAmount(
															truncated.toFixed(
																2,
															),
														);
													}}
													disabled={
														fetchingBalance ||
														usdcBalance === 0
													}
													sx={{
														minWidth: 'auto',
														px: 1.5,
														py: 0.5,
														fontSize: '12px',
														textTransform: 'none',
														fontWeight: 600,
													}}
												>
													Max
												</Button>
											),
										}}
									/>

									{/* Check if user has active positions */}
									{hasActivePosition ? (
										<Alert
											severity="warning"
											sx={{
												mt: 2,
												mb: 2,
												backgroundColor:
													'var(--atlas-bg-surface-interactive)',
												borderColor:
													'var(--atlas-border-accent)',
											}}
										>
											<Typography variant="body2">
												<strong>
													Active Position Detected
												</strong>
												<br />
												You can only have one active
												position at a time. Please
												withdraw or cancel your existing
												position to make a new deposit.
											</Typography>
										</Alert>
									) : (
										<Alert
											severity="info"
											sx={{
												mt: 2,
												mb: 2,
												backgroundColor:
													'var(--atlas-bg-surface-interactive)',
												borderColor:
													'var(--atlas-border-accent)',
											}}
										>
											<Typography
												variant="body2"
												color="textPrimary"
											>
												<strong>
													{selectedAgent.details
														.map((d) => {
															return d.name;
														})
														.toString()}
												</strong>
												<br />
												Expected: {selectedAgent.yield}
											</Typography>
										</Alert>
									)}
									{/*<TextField
										fullWidth
										label="Discount Code"
										value={discountCode}
										onChange={(e) =>
											setDiscountCode(e.target.value)
										}
										margin="normal"
										placeholder="Enter code"
										size="small"
										sx={{
											'& .MuiInputLabel-root': {
												color: 'var(--atlas-text-muted-strong)',
											},
											'& .MuiInputLabel-root.Mui-focused':
												{
													color: 'var(--atlas-text-highlight)',
												},
											'& .MuiFormHelperText-root': {
												color: 'var(--atlas-text-subtle)',
											},
											'& .MuiOutlinedInput-root': {
												color: 'var(--atlas-text-primary)',
												backgroundColor:
													'var(--atlas-bg-surface-strong)',
												'& fieldset': {
													borderColor:
														'var(--atlas-border-accent)',
												},
												'&:hover fieldset': {
													borderColor:
														'var(--atlas-border-accent-alt)',
												},
												'&.Mui-focused fieldset': {
													borderColor:
														'var(--atlas-accent-primary-alt)',
												},
												'&.Mui-disabled': {
													color: 'var(--atlas-text-subtle)',
													'& fieldset': {
														borderColor:
															'var(--atlas-border-accent-strong)',
													},
												},
												'& input::placeholder': {
													color: 'var(--atlas-text-subtle)',
													opacity: 1,
												},
											},
										}}
										InputProps={{
											endAdornment: (
												<Button
													size="small"
													onClick={() => {}}
													disabled={
														!discountCode.trim()
													}
													sx={{
														minWidth: 'auto',
														px: 1.5,
														py: 0.5,
														fontSize: '12px',
														textTransform: 'none',
														fontWeight: 600,
													}}
												>
													Apply
												</Button>
											),
										}}
									/>*/}

									<LaunchButton
										fullWidth
										size="large"
										onClick={onDeposit}
										disabled={
											true ||
											!depositAmount ||
											hasActivePosition ||
											parseFloat(depositAmount || '0') <
												minAmountAllowed ||
											parseFloat(depositAmount || '0') >
												maxAmountAllowed ||
											parseFloat(depositAmount || '0') >
												convertFromBaseUnits(
													usdcBalance,
												)
										}
										sx={{
											mt: 2,
											height: {
												xs: '44px',
												sm: '44px',
												md: '48px',
											},
											textTransform: 'none',
											fontFamily: 'Oxanium',
											fontSize: {
												xs: '15px',
												sm: '16px',
											},
											width: '100%',
										}}
										startIcon={<ArrowUpward />}
									>
										{/*Coming Soon*/}
										{hasActivePosition
											? 'Active Position - Cannot Deposit'
											: 'Start Earning'}
									</LaunchButton>
									<Typography
										color="textPrimary"
										textAlign="center"
										mt={2}
										sx={{
											fontSize: {
												xs: '13px',
												sm: '14px',
											},
										}}
									>
										{hasActivePosition
											? 'Manage your position in the active dashboard'
											: `AI agent will deploy funds using ${
													selectedAgent?.name ||
													'your selected'
											  } strategy`}
									</Typography>
								</CardContent>
							</StatsCardInnerReverse>
						</StyledCardReverse>
					</Grid>
				</Grid>
			</Box>
		</Main>
	);
};
